import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { catchError, filter, mapTo, tap } from 'rxjs/operators';
import { FlashMessagesService } from 'app/shared/flash-messages/service/flash.messages.service';
import { Injectable } from '@angular/core';
import { Map as ImmutableMap } from 'immutable';
import { LogService } from 'app/shared/log/log.service';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs/Observable';
import { empty } from 'rxjs/observable/empty';
import { PaymentSummary } from 'app/event/models/payment-summary/payment-summary';
import { PaymentHttpService } from '../payment.http.service';

@Injectable()
export class PaymentSummaryStore {

  private paymentSummaryByVendorCache: Map<number, BehaviorSubject<PaymentSummary>>;

  constructor(
    private paymentHttpService: PaymentHttpService,
    private flashMessagesService: FlashMessagesService,
    private translateService: TranslateService,
    private logService: LogService,
  ) {
    this.paymentSummaryByVendorCache = new Map<number, BehaviorSubject<PaymentSummary>>();
  }

  resolveCache(eventPartyIds: Set<number>) {
    this.resetCache();
    return this.refreshCache(eventPartyIds).pipe(
      catchError(err => {
        this.handleUserFacingError(`Error resolving payment summary cache: ${err.message || err}`);
        return empty<void>();
      }),
      mapTo(undefined as void),
    );
  }

  reloadPaymentSummary(eventPartyId: number): Observable<void>;
  reloadPaymentSummary(eventPartyIdSet: Set<number>): Observable<void>;
  reloadPaymentSummary(eventPartyIdOrSet: number | Set<number>): Observable<void> {
    if (!eventPartyIdOrSet) {
      const errorMessage = 'eventPartyIdOrSet not provided';
      this.logService.error(`Error getting Payment Summary: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    const eventPartyIds = eventPartyIdOrSet instanceof Set ?  eventPartyIdOrSet : new Set([eventPartyIdOrSet]);
    return this.refreshCache(eventPartyIds).pipe(
      catchError(err => {
        this.handleUserFacingError(`Error reloading payment summary cache item: ${err.message || err}`);
        return empty<void>();
      }),
      mapTo(undefined as void),
    );
  }

  getPaymentSummary(eventPartyId: number): Observable<PaymentSummary> {
    if (!eventPartyId) {
      const errorMessage = 'eventPartyId not provided';
      this.logService.error(`Error getting Payment Summary: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    let subject = this.paymentSummaryByVendorCache.get(eventPartyId);

    if (!subject) {
      subject = this.initSubject(eventPartyId);
      this.paymentHttpService.fetchPaymentSummary(new Set([eventPartyId])).pipe(
        tap((paymentSummaryMap: ImmutableMap<number, PaymentSummary>) => {
          const summary = paymentSummaryMap.get(eventPartyId);
          if (summary) {
            subject.next(summary);
          } else {
            this.logService.warn(`No payment summary returned for eventPartyId ${eventPartyId}`);
          }
        }),
        catchError(err => {
          this.handleSilentError(`Error lazily loading payment summary for ${eventPartyId}: ${err.message || err}`);
          return empty<void>();
        })
      ).subscribe();
    }

    return subject.asObservable().pipe(
      filter(summary => summary != null)
    );
  }

  private refreshCache(eventPartyIds: Set<number>): Observable<any> {
    if (!eventPartyIds || eventPartyIds.has(null) || eventPartyIds.has(undefined)) {
      const errorMessage = 'eventPartyId not provided';
      this.logService.error(`Error getting Payment Summary: ${errorMessage}`);
      throw new Error(errorMessage);
    }

    return this.paymentHttpService.fetchPaymentSummary(eventPartyIds).pipe(
      tap((paymentSummaryByVendor: ImmutableMap<number, PaymentSummary>) => {
        this.populateCache(paymentSummaryByVendor);
      }),
      mapTo(undefined)
    );
  }

  private resetCache(): void {
    this.paymentSummaryByVendorCache.forEach((vendorPaymentSummarySubject: BehaviorSubject<PaymentSummary>) => {
      vendorPaymentSummarySubject.complete();
    });
    this.paymentSummaryByVendorCache = new Map<number, BehaviorSubject<PaymentSummary>>();
  }

  private populateCache(paymentSummaryByVendor: ImmutableMap<number, PaymentSummary>): void {
    paymentSummaryByVendor.forEach((paymentSummary: PaymentSummary, eventPartyIdKey: number) => {
      this.upsertItem(eventPartyIdKey, paymentSummary);
    });
  }

  private upsertItem(eventPartyId: number, paymentSummary: PaymentSummary): BehaviorSubject<PaymentSummary> {
    let vendorPaymentSummarySubject = this.paymentSummaryByVendorCache.get(eventPartyId);
    if (vendorPaymentSummarySubject) {
      vendorPaymentSummarySubject.next(paymentSummary);
    } else {
      vendorPaymentSummarySubject = new BehaviorSubject<PaymentSummary>(paymentSummary);
      this.paymentSummaryByVendorCache.set(eventPartyId, vendorPaymentSummarySubject);
    }
    return vendorPaymentSummarySubject;
  }

  private initSubject(eventPartyId: number): BehaviorSubject<PaymentSummary> {
    const vendorPaymentSummarySubject = new BehaviorSubject<PaymentSummary>(null);
    this.paymentSummaryByVendorCache.set(eventPartyId, vendorPaymentSummarySubject);
    return vendorPaymentSummarySubject;
  }

  private handleUserFacingError(message: string): void {
    this.logService.error(message);
    this.flashMessagesService.error(this.translateService.instant('MESSAGES.ERROR.COULD_NOT_GET_PAYMENT_SUMMARY'));
  }

  private handleSilentError(message: string): void {
    this.logService.warn(message);
  }

}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/qfGQY7pgg/1/oneonyx/grouppay-group/grouppay/pioneer-web/src/app/event/service/payment-summary/payment-summary.store.ts